import Text from '~/components/Text/Text'
import {
  NodeBox,
  NodeLabel,
  NodePill,
  NodePillsWrapper,
  NodeStock,
  NodeValue
} from './SaF.styles'
import StockArrow from './StockArrow/StockArrow'
import shortenNumber from '~/utils/math/shortenNumber'
import { NodeColor, SaFNode } from './SaF.types'
import { useTheme } from 'styled-components'

type SaFNodeProps = Pick<
  SaFNode,
  'x0' | 'y0' | 'y1' | 'description' | 'complement' | 'note'
> & {
  inValue: number
  outValue: number
  remainingStock: number
  colors: [NodeColor, NodeColor]
  sideNumber?: boolean
}

export default function SaFNodePair({
  y0 = 0,
  y1 = 0,
  x0 = 0,
  inValue,
  outValue,
  remainingStock,
  description,
  complement,
  note,
  colors,
  sideNumber
}: SaFNodeProps) {
  const { saf } = useTheme()
  const boxHeight = Math.max(y1 - y0, saf.pillMininumBoxHeight)

  const inValueCSSVars = {
    '--baseHeight':
      outValue > inValue ? `${(100 * inValue) / outValue}%` : '100%'
  } as React.CSSProperties

  const outValueCSSVars = {
    '--baseHeight':
      inValue > outValue ? `${(100 * outValue) / inValue}%` : '100%'
  }

  return (
    <NodeBox style={{ top: y0, left: x0, height: boxHeight }}>
      <NodeLabel>
        <Text variant='content-emphasis-03'>{description}</Text>
        {complement && <Text variant='content-03'>{complement}</Text>}
        {note && <Text variant='content-02'>{note}</Text>}
      </NodeLabel>
      <NodePillsWrapper>
        <StockArrow {...{ boxHeight, inValue, outValue, remainingStock }} />

        {inValue > 0 && (
          <>
            <NodePill
              style={
                {
                  ...inValueCSSVars,
                  background: colors[0].background,
                  color: colors[0].text,
                  borderColor: colors[0].border || colors[0].background
                } as React.CSSProperties
              }
              {...{ sideNumber }}
            >
              {sideNumber ? (
                <>
                  <Text variant={'content-03'}>+</Text>
                  <Text variant={'content-03'}>{shortenNumber(inValue)}</Text>
                </>
              ) : (
                <NodeValue {...{ inValue }}>
                  <Text variant={'content-emphasis-01'}>+</Text>
                  <Text variant={'content-emphasis-01'}>
                    {shortenNumber(inValue)}
                  </Text>
                </NodeValue>
              )}
            </NodePill>
            <NodeStock
              style={{
                background: colors[0].stockBackground || colors[0].background,
                borderColor:
                  colors[0].stockBorder ||
                  colors[0].border ||
                  colors[0].stockBackground ||
                  colors[0].background
              }}
              {...{ inValue, outValue, remainingStock, sideNumber }}
            >
              <Text variant={sideNumber ? 'content-03' : 'content-emphasis-01'}>
                0
              </Text>
            </NodeStock>
          </>
        )}

        {outValue > 0 && (
          <>
            <NodePill
              style={{
                ...outValueCSSVars,
                background: colors[1].background,
                color: colors[1].text,
                borderColor: colors[1].border || colors[1].background
              }}
              isSource
              {...{ sideNumber }}
            >
              {sideNumber ? (
                <>
                  <Text variant={'content-03'}>-</Text>
                  <Text variant={'content-03'}>{shortenNumber(outValue)}</Text>
                </>
              ) : (
                <NodeValue>
                  <Text variant={'content-emphasis-01'}>-</Text>
                  <Text variant={'content-emphasis-01'}>
                    {shortenNumber(outValue)}
                  </Text>
                </NodeValue>
              )}
            </NodePill>
            <NodeStock
              style={{
                background: colors[1].stockBackground || colors[1].background,
                borderColor:
                  colors[1].stockBorder ||
                  colors[1].border ||
                  colors[1].stockBackground ||
                  colors[1].background
              }}
              isSource
              {...{ inValue, outValue, remainingStock, sideNumber }}
            >
              <Text variant={sideNumber ? 'content-03' : 'content-emphasis-01'}>
                {shortenNumber(remainingStock)}
              </Text>
            </NodeStock>
          </>
        )}
      </NodePillsWrapper>
    </NodeBox>
  )
}
